/**
 * Design tokens for the Share Shack UI.
 * All colour, spacing, and typography values in components must reference
 * these named constants — never hard-code hex values directly.
 *
 * Source of truth: REDESIGN_BRIEF.md v3.0
 */

export const T = {
  // Core palette
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceSubtle: '#F5F6F8',
  border: '#E3E5EA',
  borderStrong: '#CDD1D8',
  text: '#111318',
  textSec: '#525860',
  textMuted: '#878E98',
  accent: '#E8916A',
  accentSoft: '#FDF0EA',
  accentBorder: '#F0C4A8',

  // Semantic palette
  success: '#1A8A4A',
  successSoft: '#E6F5EC',
  successBorder: '#A8D9BC',
  successBtn: '#1A8A4A', // explicit alias for Mark as Returned CTA
  warning: '#B85C00',
  warningSoft: '#FFF3E6',
  warningBorder: '#F0C4A0',
  danger: '#DC2626',
  dangerSoft: '#FEF2F2',
  dangerBorder: '#FECACA',

  // Neutral
  white: '#FFFFFF',

  // Category palette — keyed by DB enum value
  cat: {
    TOOL: { bg: '#FFF0E6', dot: '#D4703A', text: '#8C3A10' },
    BOARD_GAME: { bg: '#E8F5EE', dot: '#3A9E68', text: '#1A5C38' },
    PUZZLE: { bg: '#EEE8FF', dot: '#7A54D4', text: '#3D1E8C' },
    EVENT_PARTY: { bg: '#FFF8E6', dot: '#C49A2A', text: '#7A5800' },
    TOY: { bg: '#E6F4FF', dot: '#3A80BF', text: '#1A4870' },
    BOOK: { bg: '#F5EEE6', dot: '#888888', text: '#5C3A1A' },
  } as Record<string, { bg: string; dot: string; text: string }>,
} as const;

/** Fallback category colours when category is unknown. */
export const CAT_FALLBACK = { bg: '#F5EEE6', dot: '#888888', text: '#5C3A1A' };

/** Emoji representation for each item category. */
export const CATEGORY_EMOJI: Record<string, string> = {
  TOOL: '🔧',
  BOARD_GAME: '🎲',
  PUZZLE: '🧩',
  BOOK: '📚',
  TOY: '🧸',
  EVENT_PARTY: '🎉',
};

/** Human-readable display labels for each item category. */
export const CATEGORY_LABEL: Record<string, string> = {
  TOOL: 'Tools',
  BOARD_GAME: 'Board Games',
  PUZZLE: 'Puzzles',
  BOOK: 'Books',
  TOY: 'Toys',
  EVENT_PARTY: 'Party Equipment',
};

/**
 * Border radius scale.
 * New structural names (sm/md/lg/xl) per REDESIGN_BRIEF v3.0.
 * Legacy semantic names (hero/card/row/button/chip/dot) kept for
 * backwards-compatibility until all components are migrated.
 */
export const RADIUS = {
  // Structural scale (use these going forward)
  sm: 8, // back buttons, small controls, export pills
  md: 12, // CTA buttons, search bar, category chips
  lg: 14, // cards, possession chain, section panels
  xl: 20, // hero panels, auth card, modals

  // Legacy aliases — do not use in new code
  hero: 20,
  card: 14,
  row: 10,
  button: 12,
  chip: 8,
  dot: 999,
} as const;

/**
 * Shadow scale per REDESIGN_BRIEF v3.0 §1.3–1.4.
 * Use as inline `style={{ boxShadow: SHADOW.md }}` or reference in
 * Tailwind config extensions.
 */
export const SHADOW = {
  sm: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
  md: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
  lg: '0 4px 16px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
  btn: {
    accent: '0 2px 8px rgba(232,145,106,0.3)',
    success: '0 2px 8px rgba(26,138,74,0.2)',
    danger: '0 2px 8px rgba(220,38,38,0.2)',
  },
} as const;

/** Spacing scale — layout and component padding/gap values. */
export const SPACE = {
  screenPadH: 20,
  screenPadTop: 28,
  sectionGap: 24,
  cardPad: 16,
  rowPadV: 12,
  rowPadH: 16,
  minTapTarget: 44,
  iconSize: 20,
} as const;

/** Responsive breakpoints (min-width values). */
export const BREAKPOINT = {
  tablet: 481,
  desktop: 769,
  /** Nav-specific cutoff: bottom nav below this width, top nav at or above. */
  nav: 768,
} as const;

/** Max container widths per breakpoint. */
export const CONTAINER = {
  mobile: 420,
  tablet: 720,
  desktop: 1000,
} as const;
